import { useCallback, useEffect, useMemo, useRef } from 'react';

import { CX_LOGIN_SUCCESS } from '~/custom-events/CXLoginSuccessEvent';
import { useModalContext } from '~/providers/modal-context';
import { useMyOCContext } from '~/providers/myoc-context';
import MyOCActions from '~/services/actions/myoc-actions';
import { type MyOCData } from '~/types/myoc';

export const MyOC = () => {
	const { invalidateMyOCUser, logoutMyOCUser, myocConfig } = useMyOCContext();
	const { bundleUrl, showFacebookLogin, showGoogleLogin, ...myocLoginConfig } =
		myocConfig;

	const [{ showMyOC }, dispatchFromContext] = useModalContext();

	useEffect(() => {
		window.toggleMyOC = () => {
			dispatchFromContext({
				type: 'TOGGLE_MYOC',
			});
		};
	}, [dispatchFromContext]);

	const myOCActions = useMemo(() => new MyOCActions(''), []);

	const initiMyOCLogin = useCallback(() => {
		try {
			window.ocRenderers['myoc-login'](myocLoginConfig, () => {
				const event = new CustomEvent('OC:ocAuthReady', {});
				window.dispatchEvent(event);
			});
		} catch (e) {
			console.log(e);
		}
	}, [myocLoginConfig]);

	const loadMyOCRef = useRef(false);

	const loadMyOCLogin = useCallback(() => {
		if (!showMyOC || loadMyOCRef.current) return;

		const script = document.createElement('script');
		script.setAttribute('src', bundleUrl);
		script.onload = initiMyOCLogin;
		document.body.appendChild(script);

		loadMyOCRef.current = true;
	}, [showMyOC, bundleUrl, initiMyOCLogin]);

	useEffect(() => {
		window.addEventListener('load', loadMyOCLogin);

		return () => {
			window.removeEventListener('load', loadMyOCLogin);
		};
	}, [loadMyOCLogin]);

	useEffect(() => {
		loadMyOCLogin();
	}, [showMyOC, loadMyOCLogin]);

	// Subscribe to OC:events
	useEffect(() => {
		const ocAuthReady = () => {
			myOCActions.toggleSocialLogins({
				facebookEnabledLocalConfig: showFacebookLogin,
				googleEnabledLocalConfig: showGoogleLogin,
			});

			window.ocAuth.listenForUserAction((e: any) => {
				const body = document.getElementsByTagName('html')[0];

				switch (e.event) {
					case 'opened':
						body?.classList?.add('body_fixed');
						break;

					// NOTE: login-success can only be triggered
					// from successful paths following modal CTA
					case 'login-success': {
						if (e.data && e.data.success) {
							// Retrieve user data from the DB through the middleware
							myOCActions
								.getUserData(e.data.token)
								.then((myoc: MyOCData) => {
									// Trigger user account personalisation flow
									const event = new CustomEvent('OC:LoggedIn', {
										detail: { myoc },
									});
									window.dispatchEvent(event);
								})
								.catch((error: Error) => {
									console.log(error);
								});
						}

						window.dispatchEvent(
							new CustomEvent(CX_LOGIN_SUCCESS, {
								detail: {
									deviceId: e.data['device-id'],
									success: true,
									token: e.data.token,
									userId: e.data['user-id'],
								},
							}),
						);
						window.dispatchEvent(new CustomEvent('OC::LOGIN_SUCCESS'));
						window.dispatchEvent(new CustomEvent('OC:InvalidateMyOC'));
						break;
					}

					case 'close': {
						body?.classList?.remove('body_fixed');
						window.dispatchEvent(new CustomEvent('OC:ModalClosed', {}));
						break;
					}
				}
			});
		};

		const ocSiteConfigLoaded = () => {
			myOCActions.toggleSocialLogins({
				facebookEnabledLocalConfig: showFacebookLogin,
				googleEnabledLocalConfig: showGoogleLogin,
			});
		};

		const ocGoogleScriptLoaded = () => {
			window.ocAuth.toggleSocialLogins({
				googleScriptLoaded: true,
			});
		};

		window.addEventListener('OC:ocAuthReady', ocAuthReady);
		window.addEventListener('OC:siteConfigLoaded', ocSiteConfigLoaded);
		window.addEventListener('OC:googleScriptLoaded', ocGoogleScriptLoaded);

		return () => {
			window.removeEventListener('OC:ocAuthReady', ocAuthReady);
			window.removeEventListener('OC:siteConfigLoaded', ocSiteConfigLoaded);
			window.removeEventListener('OC:googleScriptLoaded', ocGoogleScriptLoaded);
		};
	}, [myOCActions, showFacebookLogin, showGoogleLogin]);

	// Setup cache helpers
	useEffect(() => {
		window.addEventListener('OC:InvalidateMyOC', invalidateMyOCUser);
		window.addEventListener('OC:LogoutMyOC', logoutMyOCUser);
		return () => {
			window.removeEventListener('OC:InvalidateMyOC', invalidateMyOCUser);
			window.removeEventListener('OC:LogoutMyOC', logoutMyOCUser);
		};
	}, [invalidateMyOCUser, logoutMyOCUser]);

	return null;
};
